import { Avatar, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { LayoutGrid, Network, Activity, AlertTriangle, Flag, CalendarClock } from "lucide-react";
import HoverCard from "../../../components/hoverCard/HoverCard";
import { CurrencyIcon, PersonIcon, BriefcaseIcon } from "../../../modules/default-svg";
import { useHistory } from "react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { generalActions } from "../../../store/actions";
import { apiInstance, routes } from "../../../helpers/API";
import Modal from "../../../components/controls/modal/Modal";
import ResourceDetails from "../../resourcePlanner/resourceDetails/ResourceDetails";
import SafeHtml from "../../../components/safeHtml/SafeHtml";
import { getAccountCurrency, displayDecimalFormatted, weeksBetweenDates } from "../../../utils/systemSettings";
import TeamManagement from "../../../components/teamManagement/TeamManagement";
import ContactsPanel from "../../../components/contactsPanel/ContactsPanel";
import ExportButton from "../../../components/Export/ExportButton";
import UserOrgChartModal from "../../../components/orgChart/UserOrgChartModal";
import OrgChartTriggerButton from "../../../components/orgChart/OrgChartTriggerButton";
import HierarchyChain from "../../../components/entityDetails/HierarchyChain";
import DetailStat from "../../../components/entityDetails/DetailStat";
import entityDetailsStyles from "../../../components/entityDetails/EntityDetails.module.scss";
import portfolioService from "../../../store/services/portfolio.service";

const Details = (props) => {
  const { data, isHeaderCollapsed } = props;
  const history = useHistory();
  const { navigateTo } = useNavigation();
  const displatch = useDispatch();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showOrgChart, setShowOrgChart] = useState(false);
  const [isResourceDetailsLoading, setIsResourceDetailsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(false);
  const [resourceProjects, setResourceProjects] = useState([]);
  const [resourceTasks, setResourceTasks] = useState([]);
  const [resources, setResources] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [leftTab, setLeftTab] = useState("details");
  const [contactView, setContactView] = useState("cards");
  const leftPanelMaxHeight = `calc(100vh - ${isHeaderCollapsed ? 300 : 450}px)`;
  const setting = JSON.parse(localStorage.getItem("settings"));

  const handleRedirection = (type, id, name) => {
    switch (type) {
      case "Client": {
        // Use navigation hook for direct navigation to client
        navigateTo("Clients", id, data.client.name);
        break;
      }
      case "Project": {
        navigateTo("Projects", id, name);
        break;
      }
      default:
        return "";
    }
  };

  const buildChainNodes = () => {
    const nodes = [];
    if (data.client) {
      nodes.push({
        key: "client",
        type: "Client",
        icon: <PersonIcon />,
        name: data.client.name,
        onClick: () => handleRedirection("Client", data.client.id),
      });
    }
    nodes.push({ key: "current", type: "Portfolio", icon: <BriefcaseIcon />, name: data.name, isCurrent: true });
    return nodes;
  };

  const fetchResources = () => {
    setIsLoading(true);
    portfolioService.getTeam(data.id)
      .then((response) => {
        setResources(response);
      }
      )
      .catch((error) => { })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (data?.id) {
      fetchResources();
    }
  }, [data]);

  const getResourceContribution = async (resourceId) => {
    try {
      setIsResourceDetailsLoading(true);
      const response = await apiInstance.get(
        `${routes.getContribution.replace(":resourceId", resourceId)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage
              ?.getItem("accessToken")
              ?.replace(/"/g, "")}`,
          },
          params: {
            PortfolioId: data.id,
          },
        }
      );
      const {
        data: { resource, allocatedCapacity, totalCapacity, projects, tasks },
      } = response;
      setIsResourceDetailsLoading(false);
      setUserDetails({ ...resource, allocatedCapacity, totalCapacity });
      setResourceProjects(projects.length > 0 ? projects : []);
      setResourceTasks(tasks.length > 0 ? tasks : []);
    } catch (error) {
      setIsResourceDetailsLoading(false);
      return displatch(
        generalActions.showToast({
          title: error.message,
          type: "error",
        })
      );
    }
  };

  const handleCancel = () => setShowDetailModal(false);
  const handleOpen = (user) => {
    setUserDetails(user);
    setShowDetailModal(true);
    getResourceContribution(user.id);
  };

  return (
    <div className="DetailsTabScroll">
      <div className="row">
        {data && (
          <>
            <div className="col-lg-8">
              <div className="CustomCard p-4">
                <div className={entityDetailsStyles.panelHeaderRow}>
                  <div className="resourcesTabToggleContainer">
                    <button
                      className={`resourcesTabToggleButton ${leftTab === "details" ? "active" : ""}`}
                      onClick={() => setLeftTab("details")}
                    >
                      Details
                    </button>
                    <button
                      className={`resourcesTabToggleButton ${leftTab === "contact" ? "active" : ""}`}
                      onClick={() => setLeftTab("contact")}
                    >
                      Contacts
                    </button>
                  </div>

                  {leftTab === "contact" && (
                    <div className={entityDetailsStyles.viewToggle}>
                      <button
                        className={`${entityDetailsStyles.viewToggleButton} ${contactView === "cards" ? entityDetailsStyles.active : ""}`}
                        onClick={() => setContactView("cards")}
                      >
                        <LayoutGrid size={13} /> Cards
                      </button>
                      <span className={entityDetailsStyles.viewToggleDivider} />
                      <button
                        className={`${entityDetailsStyles.viewToggleButton} ${contactView === "orgChart" ? entityDetailsStyles.active : ""}`}
                        onClick={() => setContactView("orgChart")}
                      >
                        <Network size={13} /> Org Chart
                      </button>
                    </div>
                  )}
                </div>

                <div className="detailsLeftPanelScroll" style={{ maxHeight: leftPanelMaxHeight }}>
                  {leftTab !== "details" ? (
                    <ContactsPanel
                      embedded
                      parentType="Portfolios"
                      parentId={data.id}
                      parentName={data.name}
                      view={contactView}
                      onViewChange={setContactView}
                    />
                  ) : (
                    <>
                      <div className={entityDetailsStyles.entityHeaderRow}>
                        {data.refId && <p className="detailBadge mb-0">{data.refId}</p>}
                        <h5>{data.name}</h5>
                      </div>

                      <HierarchyChain nodes={buildChainNodes()} />

                      <div className={entityDetailsStyles.statGrid}>
                        <DetailStat icon={<Activity size={16} strokeWidth={1.5} />} label="Status" value={data.status} />
                        <DetailStat
                          icon={<CalendarClock size={16} strokeWidth={1.5} />}
                          label="Duration"
                          value={data.startDate && data.endDate ? weeksBetweenDates(data.startDate, data.endDate) : null}
                        />
                        <DetailStat
                          icon={<CurrencyIcon color="#342C82" />}
                          label="Budget"
                          value={
                            data.budget
                              ? `${getAccountCurrency(setting)}${displayDecimalFormatted(setting, data.budget)}`
                              : null
                          }
                        />
                        <DetailStat icon={<AlertTriangle size={16} strokeWidth={1.5} />} label="Risk Level" value={data.riskLevel} />
                        {/* <DetailStat icon={<Flag size={16} strokeWidth={1.5} />} label="Priority" value={data.priority} /> */}
                      </div>
                      {data.stakeholders && (
                        <div className={entityDetailsStyles.descriptionCard}>
                          <h6 className={entityDetailsStyles.descriptionTitle}>Stakeholders</h6>
                          <p className={entityDetailsStyles.descriptionText}>
                            <SafeHtml html={data.stakeholders} />
                          </p>
                        </div>
                      )}
                      {data.description && (
                        <div className={entityDetailsStyles.descriptionCard}>
                          <h6 className={entityDetailsStyles.descriptionTitle}>Description</h6>
                          <p className={entityDetailsStyles.descriptionText}>
                            <SafeHtml html={data.description} />
                          </p>
                        </div>
                      )}
                      {data.objectives && (
                        <div className={entityDetailsStyles.descriptionCard}>
                          <h6 className={entityDetailsStyles.descriptionTitle}>Objectives</h6>
                          <p className={entityDetailsStyles.descriptionText}>
                            <SafeHtml html={data.objectives} />
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="CustomCard p-4">
                <div className="d-flex justify-content-between align-items-center mb-20">
                  <div className="d-flex align-items-center gap-2">
                    <OrgChartTriggerButton onClick={() => setShowOrgChart(true)} />
                    <h4 className="mb-0">Team</h4>
                    <div className="resourcesTabToggleContainer">
                      <span
                        className={`resourcesTabProject`}
                      >
                        By Project
                      </span>
                    </div>
                  </div>

                  <ExportButton config={{ category: 6, portfolioId: data.id }} />
                </div>

                <div className="detailsLeftPanelScroll" style={{ maxHeight: leftPanelMaxHeight }}>
                {isLoading ? (
                  [...Array(5)].map((_, index) => (
                    <div key={index} className="d-flex align-items-center CustomCardWrap pl-2 mb-3">
                      <Skeleton>
                        <SkeletonItem size={32} shape="circle" />
                      </Skeleton>
                      <div className="ms-12 flex-grow-1">
                        <Skeleton>
                          <SkeletonItem style={{ width: '60%', height: '20px' }} />
                        </Skeleton>
                      </div>
                    </div>
                  ))
                ) : (
                  resources && resources.resourcesByProject &&
                  resources.resourcesByProject.map((item) => (
                    <div key={item.project.id} className="bg-[#f9f8ff] rounded-lg p-3 mb-3">
                      <p
                        className="text-[13px] text-[#342c82] font-figtree font-semibold mb-2 hover:font-bold cursor-pointer"
                        onClick={() => { handleRedirection("Project", item.project.id, item.project.name) }}>
                        {item.project.name}
                      </p>
                      {item.users.map((user) => (
                        <div
                          className="d-flex align-items-center CustomCardWrap pl-2"
                          key={item.id}
                        >
                          <HoverCard
                            HoverCardTrigger={
                              <Avatar
                                color="colorful"
                                size={28}
                                name={user.name}
                                image={{ src: user.image ? user.image : "" }}
                              />
                            }
                            AvatarId={user.id}
                            AvatarName={user.name}
                            AvatarImage={user.image}
                            AvatarDesignation={user.title}
                            AvatarEmail={user.email}
                            AvatarPhone={user.phoneNumber}
                            AvatarIsExternal={user.isExternal}
                          />
                          {/* <div className="ms-12 hover:font-bold hover:underline" onClick={() => handleOpen(user)}>
                            <p className="mb-1 textColor3">{user.name}</p>
                          </div> */}
                          <div className="ms-12 resourceNameContainer">
                            <span className="resourceName">{user.name}</span>
                            {user?.title && <span className="resourceTitle">{user.title}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showDetailModal && (
        <Modal
          title="Resource Details"
          footer={false}
          handleCancel={() => handleCancel(false)}
          handleSave={() => handleCancel(false)}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          isLoading={false}
          size="large"
          calendarModalWrap={true}
          body={
            <ResourceDetails
              isLoading={isResourceDetailsLoading}
              userDetails={userDetails}
              shift={null}
              projects={resourceProjects}
              tasks={resourceTasks}
              isProjects={true}
              isTasks={true}
              defaultTab="projects"
            />
          }
        />
      )}

      {showOrgChart && (
        <UserOrgChartModal
          entityType="portfolio"
          entityId={data.id}
          entityName={data.name}
          onClose={() => setShowOrgChart(false)}
        />
      )}
    </div>
  );
};
export default Details;
